import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import axios from "axios";
import Select from "react-select";
import { ROLL_CALL_API_BASE_URL } from "../RollCall/RollCallConfig/RollCallConfig";

const AddVisit = ({ isOpen, onClose, onAdd }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [tableFilteredEmployees, setTableFilteredEmployees] = useState([]);
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFullDay, setIsFullDay] = useState(false);
  // console.log(tableFilteredEmployees)
  const getCurrentDateTimeLocal = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };
  const getInitialInfo = () => ({
    title: "",
    date: getCurrentDateTimeLocal(),
    expectedDuration: "60",
    visitType: "meeting",
    personToMeetEmpId: "",
    requestedByEmpId: userData?.RegId || "",
    CompanyName: "",
  });
  const [info, setInfo] = useState(getInitialInfo);

  const navigate = useNavigate();
  const visitTypeOptions = [
    { value: "meeting", label: "Meeting" },
    { value: "client", label: "Client" },
    { value: "vendor", label: "Vendor" },
    { value: "candidate", label: "Candidate / Interview" },
    { value: "other", label: "Other" },
  ];
  const durationOptions = [
    { value: "30", label: "30 Minutes" },
    { value: "60", label: "1 Hour" },
    { value: "120", label: "2 Hours" },
    { value: "180", label: "3 Hours" },
  ];

  const visitTypeMap = {
    meeting: 1,
    client: 2,
    vendor: 3,
    candidate: 4,
    other: 5,
  };

  const [visitors, setVisitors] = useState([
    { firstName: "", lastName: "", mobile: "", email: "", vehicleNo: "" },
  ]);

  const handleInfoChange = (e) => {
    setInfo({ ...info, [e.target.name]: e.target.value });
  };

  const handleVisitorChange = (index, field, value) => {
    const updated = [...visitors];
    updated[index][field] = value;
    setVisitors(updated);
  };

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");
    const userDataString = sessionStorage.getItem("userData");
    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
      if (userDataString) {
        setUserData(JSON.parse(userDataString));
      }
    } else {
      console.warn("User data not found in sessionStorage");
      navigate("/signin");
    }
  }, [navigate]);

  useEffect(() => {
    if (userData?.RegId) {
      setInfo((prev) => ({
        ...prev,
        requestedByEmpId: userData.RegId,
      }));
    }
  }, [userData]);

  useEffect(() => {
    if (!superIdData || !tokenData) return;

    const fetchEmployees = async () => {
      try {
        const response = await axios.get(
          `${ROLL_CALL_API_BASE_URL}portal/emp/getemployeedropdown`,
          {
            params: {
              superid: superIdData,
              BranchId: 0,
              DeptId: 0,
            },
            headers: {
              Authorization: `Bearer ${tokenData}`,
            },
          },
        );

        setTableFilteredEmployees(
          response.data.status ? response.data.ResultData : [],
        );
      } catch (error) {
        console.error("Employee fetch error:", error);
        setTableFilteredEmployees([]);
      }
    };

    fetchEmployees();
  }, [superIdData, tokenData]);

  useEffect(() => {
    if (!info.visitType || !info.personToMeetEmpId) return;

    const selectedEmployee = tableFilteredEmployees.find(
      (emp) => String(emp.RegId) === String(info.personToMeetEmpId),
    );

    if (!selectedEmployee) return;

    const visitTypeLabels = {
      meeting: "Meeting",
      client: "Client meeting",
      vendor: "Vendor discussion",
      candidate: "Interview",
      other: "Discussion",
    };

    const autoPurpose = `${visitTypeLabels[info.visitType] || "Meeting"} with ${selectedEmployee.UserWithBadge}`;

    setInfo((prev) => ({
      ...prev,
      title: autoPurpose,
    }));
  }, [info.visitType, info.personToMeetEmpId, tableFilteredEmployees]);

  const handleSubmit = async () => {
    if (
      !info.title ||
      !info.date ||
      !info.personToMeetEmpId ||
      !info.requestedByEmpId
    ) {
      Swal.fire("Warning", "Please fill all required fields", "warning");
      return;
    }

    if (!visitors[0].firstName || !visitors[0].mobile) {
      Swal.fire("Error", "Visitor Name and Mobile required", "error");
      return;
    }
    setLoading(true);
    try {
      // const dateObj = new Date(info.date);
      const transactionDate = info.date.split("T")[0];
      const startDateTime = info.date + ":00";
      const payload = {
        SuperId: superIdData,
        VisitorName: visitors[0].firstName,
        Mobile: visitors[0].mobile,
        Email: visitors[0].email || "",
        VehicleNo: visitors[0].vehicleNo || "",
        TransactionDate: transactionDate,
        Duration: isFullDay ? 0 : Number(info.expectedDuration),
        StartDateTime: startDateTime,
        VisitType: visitTypeMap[info.visitType],
        HostedBy: Number(info.personToMeetEmpId),
        RequestedBy: Number(info.requestedByEmpId),
        CompanyName: info.CompanyName,
        Purpose: info.title,
        CreatedBy: userData?.RegId,
      };

      const response = await axios.post(
        `${ROLL_CALL_API_BASE_URL}Visitor/Pass/Addvisitor`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenData}`,
          },
        },
      );

      if (response.data.status) {
        Swal.fire("Success", "Visit Created Successfully", "success").then(
          () => {
            handlecancel();
            onClose();
            if (onAdd) onAdd();
          },
        );
      } else {
        Swal.fire("Error", "Failed to create visit", "error");
      }
    } catch (error) {
      console.error("Add Visit Error:", error);
      Swal.fire("Error", "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlecancel = () => {
    setInfo(getInitialInfo());
    setVisitors([
      { firstName: "", lastName: "", mobile: "", email: "", vehicleNo: "" },
    ]);
    setIsFullDay(false);
    setCurrentStep(1);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`offcanvas-backdrop fade ${isOpen ? "show" : ""}`}
        style={{ pointerEvents: isOpen ? "auto" : "none" }}
        onClick={onClose}
      />

      <div
        className={`offcanvas offcanvas-end ${isOpen ? "show" : ""}`}
        id="offcanvasRightApplyVisit"
        tabIndex={-1}
        style={{
          minWidth: "38%",
          maxWidth: "38%",
          visibility: isOpen ? "visible" : "hidden",
        }}
      >
        <div className="offcanvas-header py-3">
          <h4 className="mb-0">Apply Visiting Pass</h4>
          <button
            type="button"
            className="btn-close"
            onClick={() => {
              handlecancel();
              onClose();
            }}
          />
        </div>

        <hr className="my-1" />

        <div className="offcanvas-body pt-0">
          {/* Tabs – kept original structure */}
          <ul className="nav nav-tabs nav-tabs-bottom mb-3"></ul>

          {/* ─── STEP 1 ─── */}
          {currentStep === 1 && (
            <div>
              <div className="mt-2">
                <h6 className="mb-2">Visitor Details</h6>
                <div className="border rounded p-2 mb-3 bg-light">
                  <div className="d-flex justify-content-between mb-2">
                    <span>
                      Visitor <span className="text-danger">*</span>
                    </span>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <input
                        className="form-control form-control-sm"
                        placeholder="Visitor Name *"
                        value={visitors[0]?.firstName || ""}
                        onChange={(e) =>
                          handleVisitorChange(0, "firstName", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <input
                        className="form-control form-control-sm"
                        placeholder="Mobile *"
                        value={visitors[0]?.mobile || ""}
                        onChange={(e) =>
                          handleVisitorChange(0, "mobile", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        placeholder="Email"
                        value={visitors[0]?.email || ""}
                        onChange={(e) =>
                          handleVisitorChange(0, "email", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <input
                        className="form-control form-control-sm"
                        placeholder="Vehicle No"
                        value={visitors[0]?.vehicleNo || ""}
                        onChange={(e) =>
                          handleVisitorChange(0, "vehicleNo", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-2">
                  <label className="form-label">
                    Date & Time <span className="text-danger">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    name="date"
                    value={info.date}
                    onChange={handleInfoChange}
                  />
                </div>
                <div className="col-md-6 mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label mb-0">Duration</label>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="fullDayCheck"
                        checked={isFullDay}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setIsFullDay(checked);

                          if (checked) {
                            setInfo((prev) => ({
                              ...prev,
                              expectedDuration: null,
                            }));
                          } else {
                            setInfo((prev) => ({
                              ...prev,
                              expectedDuration: "60",
                            }));
                          }
                        }}
                      />
                      <label
                        className="form-check-label ms-1"
                        htmlFor="fullDayCheck"
                      >
                        All day
                      </label>
                    </div>
                  </div>

                  <Select
                    options={durationOptions}
                    value={
                      durationOptions.find(
                        (option) => option.value === info.expectedDuration,
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      setInfo((prev) => ({
                        ...prev,
                        expectedDuration: selectedOption
                          ? selectedOption.value
                          : "",
                      }))
                    }
                    placeholder="-- Select Duration --"
                    isClearable
                    isDisabled={isFullDay}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-2">
                  <label className="form-label">
                    Visit Type <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={visitTypeOptions}
                    value={
                      visitTypeOptions.find(
                        (option) => option.value === info.visitType,
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      setInfo((prev) => ({
                        ...prev,
                        visitType: selectedOption ? selectedOption.value : "",
                      }))
                    }
                    placeholder="-- Select Visit Type --"
                    isClearable
                  />
                </div>
                <div className="col-md-6 mb-2">
                  <label className="form-label">
                    Meeting With <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={tableFilteredEmployees.map((emp) => ({
                      value: emp.RegId,
                      label: emp.UserWithBadge,
                    }))}
                    value={
                      tableFilteredEmployees
                        .map((emp) => ({
                          value: emp.RegId,
                          label: emp.UserWithBadge,
                        }))
                        .find(
                          (option) =>
                            String(option.value) ===
                            String(info.personToMeetEmpId),
                        ) || null
                    }
                    onChange={(selectedOption) =>
                      setInfo((prev) => ({
                        ...prev,
                        personToMeetEmpId: selectedOption
                          ? selectedOption.value
                          : "",
                      }))
                    }
                    placeholder="-- Search & Select --"
                    isClearable
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-2">
                  <label className="form-label">
                    Requested By <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={tableFilteredEmployees.map((emp) => ({
                      value: emp.RegId,
                      label: emp.UserWithBadge,
                    }))}
                    value={
                      tableFilteredEmployees
                        .map((emp) => ({
                          value: emp.RegId,
                          label: emp.UserWithBadge,
                        }))
                        .find(
                          (option) =>
                            String(option.value) ===
                            String(info.requestedByEmpId),
                        ) || null
                    }
                    onChange={(selectedOption) =>
                      setInfo((prev) => ({
                        ...prev,
                        requestedByEmpId: selectedOption
                          ? selectedOption.value
                          : "",
                      }))
                    }
                    placeholder="-- Search & Select --"
                    isClearable
                  />
                </div>
                <div className="col-md-6 mb-2">
                  <label className="form-label">Company Name</label>
                  <input
                    type="text"
                    name="CompanyName"
                    className="form-control"
                    placeholder="Enter Company Name"
                    value={info.CompanyName || ""}
                    onChange={handleInfoChange}
                  />
                </div>
              </div>
              <div className="mb-2">
                <label className="form-label">
                  Purpose <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  placeholder="Enter purpose"
                  name="title"
                  value={info.title}
                  onChange={handleInfoChange}
                />
              </div>
            </div>
          )}
          {/* Footer */}
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button
              className="btn btn-secondary"
              onClick={() => {
                handlecancel();
                onClose();
              }}
            >
              Cancel
            </button>

            <button
              className="btn btn-success"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                  />
                  Submitting...
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

AddVisit.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

export default AddVisit;
